import classNames from 'classnames';
import React, { useContext, useEffect, useState } from 'react';
import { App } from '@typedream/data';
import OptionsContainer from '../../../../Options';
import styles from '../CollectionSettings.module.css';
import { ICollectionData } from '../../../../../types';
import { CollectionSettingsDataContext } from '../contextProvider';
import { TextEditorContext } from '../../../context/TextEditorContext';
import TextEditor from '../../..';
import { EditorEditableContext } from '../../../context/EditorEditableContextProvider';
import {
  BLOCK_ELEMENT_KEY, BLOCK_TYPE, ICollectionTemplate,
} from '../../../types';
import { initialValueEmpty } from '../../../config';
import { updateCollectionItem } from '../../../../../utils/updateCollection';
import TokenContext from '../../../../../utils/jwt/tokenContext';
import CollectionShowSchemaProvider from '../../../context/CollectionSettingsContextProvider/showSchemaProvider';
import APIProvider from '../../../context/APIContextProvider';
import { BUCKET_URL, BUCKET_URL2, EDITOR_API_BASE_URL } from '../../../../../constants';
import { errToString } from '../../../../../utils/errors';
import { useGetSpaceQuery, usePagesPaths } from '../../../../../store';
import { useSpaceAndPageIDFromRouter } from '../../../../../data/space/hooks';
import { api } from '../../../../../data/api';
import DreamProvider from '../../../provider';

function Preview({ elementTemplate, editorID }:
  { elementTemplate: ICollectionTemplate, editorID: string }) {
  const [viewData, setviewData] = useState(false);
  const [value, setvalue] = useState(initialValueEmpty);
  const [spaceID] = useSpaceAndPageIDFromRouter();
  const { data: space } = useGetSpaceQuery([spaceID]);
  const pagesPaths = usePagesPaths(spaceID);
  const {
    collectionData,
    listColumns,
    element,
  } = useContext(CollectionSettingsDataContext);
  const { spacePermissionTokenHandler, subscriptionTokenHandler } = useContext(
    TokenContext,
  );

  const getPreviewData = async () => {
    const notionDataResults = await new App(api().api).app(collectionData)?.getData(
      spaceID,
      collectionData,
      undefined,
      spacePermissionTokenHandler,
      subscriptionTokenHandler,
    );
    const data: ICollectionData = {
      [element?.[BLOCK_ELEMENT_KEY.COLLECTION_DATA_ID]]: {
        integration: collectionData.integration,
        metadata: {},
        data: notionDataResults?.data,
      },
    };

    const elementCopy = { ...element };
    elementCopy[BLOCK_ELEMENT_KEY.COLLECTION_TEMPLATE] = elementTemplate;
    const dataID = elementCopy?.[BLOCK_ELEMENT_KEY.COLLECTION_DATA_ID];
    let collection = null;
    if (dataID) {
      const currData = data[dataID]?.data;
      if (currData) {
        const template = elementTemplate?.template;
        const schema = elementTemplate?.schema;
        if (currData) {
          const spaceCollectionData = space?.customization?.collection_item_data;
          const itemPageDataID = new App(api().api).app(
            data?.[dataID],
          )?.getItemPageDataID(data?.[dataID]);
          collection = currData.map((row) => {
            const collectionItem = updateCollectionItem(
              row,
              schema,
              template as any,
              spaceCollectionData?.[itemPageDataID]?.childrenTemplatePageID,
              pagesPaths[spaceCollectionData?.[itemPageDataID]?.childrenTemplatePageID]?.path,
            );
            return ({
              children: [
                collectionItem,
              ],
              type: BLOCK_TYPE.COLLECTION_ITEM,
            });
          });
        }
      }
    }
    elementCopy.children = collection;

    const elementValue = [{
      children: [elementCopy],
    } as any];
    setvalue(elementValue);
  };
  useEffect(() => {
    if (!viewData) {
      const numCol = elementTemplate?.settings?.numCol;
      const templateChildren = Array.from(Array(numCol).keys()).map(() => ({
        children: [
          elementTemplate?.template as any,
        ],
        type: BLOCK_TYPE.COLLECTION_ITEM,
      }));
      const templateValue = [{
        children: [
          {
            children: [
              ...templateChildren,
            ],
            type: BLOCK_TYPE.COLLECTION,
            [BLOCK_ELEMENT_KEY.COLLECTION_TEMPLATE]: elementTemplate,
            [BLOCK_ELEMENT_KEY.WIDTH_MAX]: '100%',
          },
        ],
      } as any];
      setvalue(templateValue);
    } else {
      getPreviewData();
    }
  }, [viewData, element, elementTemplate, elementTemplate?.settings?.numCol]);

  useEffect(() => {
    listColumns(collectionData);
  }, [collectionData]);

  return (
    <div className={classNames(styles.fieldsToDisplayPreview)}>
      <div className={classNames(styles.fieldsToDisplayPreviewBlock)}>
        <div className={classNames(styles.previewModeContainer)}>
          <div
            className={styles.previewModeHighlight}
            style={viewData ? { left: 'calc(100% - 110px)', width: 105 } : { left: 5, width: 83 }}
          />
          <button
            type="button"
            className={classNames('is-size-7 with-lighter-text-color', styles.previewMode, {
              [styles.previewModeActive]: !viewData,
            })}
            onClick={() => setviewData(false)}
          >
            Template
          </button>
          <button
            type="button"
            className={classNames('is-size-7 with-lighter-text-color', styles.previewMode, {
              [styles.previewModeActive]: viewData,
            })}
            onClick={() => setviewData(true)}
          >
            Preview Data
          </button>
        </div>
      </div>
      <OptionsContainer
        selected={false}
        className={styles.preview}
      >
        <APIProvider
          assetsBaseURL={[BUCKET_URL, BUCKET_URL2]}
          documentBaseURL={EDITOR_API_BASE_URL}
          errToString={errToString}
        >
          <EditorEditableContext.Provider value={false}>
            <DreamProvider id={`collection-settings-preview-${editorID}`}>
              <TextEditorContext.Provider value={{
                initialValue: value,
                save: () => null,
                setInitialValue: () => null,
                isDraft: false,
                loading: false,
              }}
              >
                <CollectionShowSchemaProvider showSchema={!viewData}>
                  <TextEditor id={`collection-settings-preview-${editorID}`} navbar={false} />
                </CollectionShowSchemaProvider>
              </TextEditorContext.Provider>
            </DreamProvider>
          </EditorEditableContext.Provider>
        </APIProvider>
      </OptionsContainer>
    </div>
  );
}

export default Preview;
